import { getUser as getStoredUser } from './tenantStorage';

/**
 * Format a time string (HH:MM or HH:MM:SS) for display based on tenant preference.
 * @param {string} time - Time string in 24h format (e.g., "17:00" or "17:00:00")
 * @param {string} format - '12h' for AM/PM or '24h' for military time
 * @returns {string} Formatted time string
 */
export const formatTimeDisplay = (time, format = '12h') => {
  if (!time) return '';
  if (format === '24h') return time.substring(0, 5);
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

const DEFAULT_TIMEZONE = 'America/New_York';
const tzValidity = new Map(); // Intl construction is not free; callers run per render/per row

/** Same test as server/utils/timezoneUtils.js isValidTimezone. */
export const isValidTimezone = (tz) => {
  if (!tz || typeof tz !== 'string') return false;
  if (!tzValidity.has(tz)) {
    let ok = true;
    try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); } catch { ok = false; }
    tzValidity.set(tz, ok);
  }
  return tzValidity.get(tz);
};

/**
 * MUST mirror server/utils/timezoneUtils.js resolveUserTimezone exactly:
 * valid user timezone -> valid tenant timezone -> America/New_York.
 * Each candidate is validated independently.
 */
export const resolveTimezone = (userTz, tenantTz) => {
  if (isValidTimezone(userTz)) return userTz;
  if (isValidTimezone(tenantTz)) return tenantTz;
  return DEFAULT_TIMEZONE;
};

/** Account timezone for display and punch payloads (user -> company -> America/New_York). */
export const getUserTimezone = () => {
  try {
    const user = getStoredUser() || {};
    return resolveTimezone(user.timezone, user.tenantTimezone);
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

/** Company (tenant) timezone from the stored login: tenantTimezone -> America/New_York.
 *  For tenant-facing calendar dates such as invoice dates (server: tenants.timezone). */
export const getTenantTimezone = () => {
  try {
    const user = getStoredUser() || {};
    return resolveTimezone(null, user.tenantTimezone);
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

/** Format an ISO timestamp as a local time string in the given timezone. */
export const formatTimeInTimezone = (timestamp, timezone) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return '';
  }
};

/**
 * Format a full ISO timestamp (e.g. `completed_at`) as a "MM/DD/YYYY" calendar
 * date in the given timezone. Unlike a plain 'YYYY-MM-DD' field, a
 * timestamp's calendar date is zone-dependent — slicing the ISO string or
 * reading the browser's local Date getters can land on the wrong day (an
 * evening Eastern timestamp is already "tomorrow" in UTC).
 */
export const formatDateInTimezone = (timestamp, timezone) => {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: '2-digit', day: '2-digit', year: 'numeric',
      timeZone: timezone,
    });
  } catch {
    return '';
  }
};

/**
 * Format an ISO timestamp (e.g. a task's `planned_start`) as "Thu 9/17, 9:00 AM"
 * in the given timezone — weekday and date formatted separately and joined by
 * hand, since Intl inserts its own comma between them ("Thu, 9/17") otherwise.
 */
export const formatPlannedDateTime = (timestamp, timezone) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: timezone });
    const monthDay = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: timezone });
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone });
    return `${weekday} ${monthDay}, ${time}`;
  } catch {
    return '';
  }
};

// The wall-clock offset of `tz` from UTC, in minutes, AT the given instant
// (so it's DST-correct for that specific moment): local wall-clock time =
// utcMs + offsetMinutes. Native Intl only, no date library.
const getZoneOffsetMinutes = (date, tz) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = {};
  dtf.formatToParts(date).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
  // Re-reading the zoned wall-clock digits as if they were UTC gives an
  // instant offset from `date` by exactly the zone's current UTC offset.
  const asUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second)
  );
  return (asUtc - date.getTime()) / 60000;
};

/**
 * The wall-clock date and time an ISO timestamp falls on in `tz`, shaped for
 * <input type="date"> / <input type="time"> — the zoned counterpart to
 * reading a Date's local (browser-timezone) getters. Falls back to empty
 * fields (rather than throwing) on a bad timestamp or an unrecognized
 * timezone, same as this file's other formatters — a caller like the Plan
 * dialog's openPlanDialog should treat that the same as "nothing to prefill".
 */
export const getZonedDateTimeParts = (iso, tz) => {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
    const parts = {};
    dtf.formatToParts(new Date(iso)).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
    if (!parts.year) throw new Error('unparsable');
    return {
      date: `${parts.year}-${parts.month}-${parts.day}`,
      time: `${parts.hour}:${parts.minute}`,
    };
  } catch {
    return { date: '', time: '' };
  }
};

/** Today's calendar date "YYYY-MM-DD" in `tz` (invalid -> America/New_York), for
 *  <input type="date"> defaults. Not new Date().toISOString(): that is the UTC day,
 *  which is already tomorrow on a US evening. */
const todayFormatters = new Map(); // one Intl.DateTimeFormat per timezone (construction is slow)
export const todayInTimezone = (tz, now = new Date()) => {
  const zone = resolveTimezone(null, tz);
  try {
    let dtf = todayFormatters.get(zone);
    if (!dtf) {
      dtf = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' });
      todayFormatters.set(zone, dtf);
    }
    const parts = {};
    dtf.formatToParts(now).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    return '';
  }
};

/**
 * Interpret a "YYYY-MM-DD" + "HH:MM" pair as wall-clock time IN `tz` (not
 * whatever zone the browser happens to be in) and return the equivalent UTC
 * ISO string. Guesses the UTC instant using the zone's offset at a first
 * pass, then re-checks the offset at that instant and corrects once — the
 * first guess can land on the wrong side of a DST transition.
 *
 * A wall time that doesn't exist (the spring-forward gap, e.g. 2:30 AM on the
 * "spring forward" day) resolves as if the pre-transition offset still
 * applied, landing one hour earlier than the wall-clock digits suggest; a
 * wall time that occurs twice (the fall-back overlap) resolves to its first
 * occurrence. Neither case is disambiguated further — planning a task for
 * one specific minute of one specific day doesn't need to be exact within an
 * hour on the two days a year this could matter.
 */
export const zonedWallTimeToUtcIso = (dateStr, timeStr, tz) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const targetWallMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  const offset1 = getZoneOffsetMinutes(new Date(targetWallMs), tz);
  let utcMs = targetWallMs - offset1 * 60000;
  const offset2 = getZoneOffsetMinutes(new Date(utcMs), tz);
  if (offset2 !== offset1) {
    utcMs = targetWallMs - offset2 * 60000;
  }
  return new Date(utcMs).toISOString();
};
